import React, { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { useMutation } from "@apollo/client";
import {
  UpdateChallengeProgressStatus,
  UpdateChallengeProgressStatusVariables,
} from "../../../types/__generated__/UpdateChallengeProgressStatus";
import { UPDATE_CHALLENGE_PROGRESS_STATUS } from "../../../graphql/resolvers/badge";
import { showErrorAlert } from "../../../helpers/alerts";
import { logError } from "../../../helpers/logging";
import StyledLoading from "../Loading/StyledLoading";

import badgeDefault from "../../../assets/images/icons/badge-default.svg";
import { colors } from "../../../styles/variables";
import { DB_ID_CHALLENGE_STATUS_QUIT, I18N_NAMESPACES } from "../../../helpers/constants";
import { ChallengeBadges_memberBadgesByCategory } from "../../../types/__generated__/ChallengeBadges";
import ImageAndTextListCell from "../ImageAndTextListCell";
import StyledSideTray from "../SideTray/StyledSideTray";
import { formatDate } from "../../../helpers/dates";
import {
  BackArrow,
  Header,
  ChallengeDescriptionTitle,
  ChallengeDescriptionTableImageWrapper,
  ChallengeBadgeImageBackground,
  ChallengeBadgeImage,
  ChallengeNameTitle,
  ChallengeStartTime,
  TextComponent,
  GameplayButton,
  QuitButton,
} from "./style";

interface ChallengeOptionsProps {
  challengeBadge: ChallengeBadges_memberBadgesByCategory | null | undefined;
  onOpenChallengeGameplay: Function;
  onUpdateChallengeStatus: Function;
  onClose: Function;
  isInBoxAndCartTray?: boolean;
}

const ChallengeOptions = ({
  challengeBadge,
  onOpenChallengeGameplay,
  onUpdateChallengeStatus,
  onClose,
  isInBoxAndCartTray = false,
}: ChallengeOptionsProps) => {
  const { t } = useTranslation([I18N_NAMESPACES.COMMON, I18N_NAMESPACES.MY_BADGES]);
  const { isEarned } = challengeBadge || {};
  const { challengeGameplayTitle1 } = challengeBadge || {};
  const hasGameplayContent = !!challengeGameplayTitle1;

  /**
   * If the badge has been earned, the date badge was achieved is returned.
   * If the badge hasn't been earned, the date it was started is returned.
   * Finally, if the badge hasn't been achieved nor started, this function returns an empty string.
   */
  const getDateToDisplay = (): string => {
    let dateToDisplay = "";

    const dateAchieved = challengeBadge?.dateTimeEarned;
    if (dateAchieved) {
      const date = formatDate(dateAchieved, t("common:timeFormat.date"));
      const time = formatDate(dateAchieved, t("common:timeFormat.timeHourAndMinute"));
      dateToDisplay = t("myBadges:achievedDateAndTime", {
        date,
        time,
        interpolation: { escapeValue: false },
      });
      return dateToDisplay;
    }

    const dateTimeStarted = challengeBadge?.challengeProgress?.dateTimeStarted;
    if (dateTimeStarted) {
      const date = formatDate(dateTimeStarted, t("common:timeFormat.date"));
      const time = formatDate(dateTimeStarted, t("common:timeFormat.timeHourAndMinute"));
      dateToDisplay = t("myBadges:startedDateAndTime", {
        date,
        time,
        interpolation: { escapeValue: false },
      });
    }

    return dateToDisplay;
  };

  const dateToDisplay = getDateToDisplay();

  const [updateChallengeProgressStatus, { loading: isUpdateChallengeStatusLoading }] = useMutation<
    UpdateChallengeProgressStatus,
    UpdateChallengeProgressStatusVariables
  >(UPDATE_CHALLENGE_PROGRESS_STATUS);

  const submitQuitChallenge = async () => {
    if (!challengeBadge?.id) {
      return;
    }

    try {
      const { data: challengeStatusData } = await updateChallengeProgressStatus({
        variables: {
          input: {
            badgeId: challengeBadge?.id,
            challengeProgressId: challengeBadge?.challengeProgress?.id,
            desiredChallengeStatusId: DB_ID_CHALLENGE_STATUS_QUIT,
          },
        },
      });

      const {
        errors,
        isSuccessful,
        challengeProgress: progress,
      } = challengeStatusData?.updateChallengeProgressStatus || {};

      if (errors || !progress || !isSuccessful) {
        const error = errors?.[0]?.messages?.[0] || t("common:sorry");
        showErrorAlert(error, {});
        throw Error(error);
      }
      onUpdateChallengeStatus();
      if (isSuccessful) {
        onClose();
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      logError(error, { location: "ChallengeOptions", action: "UPDATE_CHALLENGE_PROGRESS_STATUS" });
    }
  };

  const renderEarnPointsImage = (): ReactElement => (
    <ChallengeDescriptionTableImageWrapper>
      <ChallengeBadgeImageBackground>
        <ChallengeBadgeImage src={challengeBadge?.imageSmallUrl ?? badgeDefault} />
      </ChallengeBadgeImageBackground>
    </ChallengeDescriptionTableImageWrapper>
  );

  const renderTextComponent = (): ReactElement => (
    <TextComponent>
      <ChallengeNameTitle>{challengeBadge?.name ?? ""}</ChallengeNameTitle>
      <ChallengeStartTime>{dateToDisplay}</ChallengeStartTime>
    </TextComponent>
  );

  const renderQuitButton = (): ReactElement => (
    <QuitButton ghost outline lg onClick={() => submitQuitChallenge()}>
      {t("myBadges:quit")}
    </QuitButton>
  );

  const renderGameplayButton = (): ReactElement => (
    <GameplayButton ghost lg outline onClick={() => onOpenChallengeGameplay()}>
      {t("myBadges:gameplay")}
    </GameplayButton>
  );

  /**
   * Renders the content for challenge options side tray.
   * */
  const renderContent = (): ReactElement => (
    <>
      <ImageAndTextListCell
        imageComponent={renderEarnPointsImage()}
        textComponent={renderTextComponent()}
        bgColor={colors.peachTint}
      />
      {hasGameplayContent && renderGameplayButton()}
      {!isEarned && renderQuitButton()}
    </>
  );

  return (
    <StyledSideTray
      isOpened={!!challengeBadge}
      color={colors.white}
      shouldAnimate
      onClickBackdrop={() => onClose()}
      isInBoxAndCartTray={isInBoxAndCartTray}
    >
      <Header>
        <BackArrow onClick={() => onClose()} />
        <ChallengeDescriptionTitle>{t("myBadges:options")}</ChallengeDescriptionTitle>
      </Header>
      {isUpdateChallengeStatusLoading ? <StyledLoading /> : renderContent()}
    </StyledSideTray>
  );
};

export default ChallengeOptions;
